import { useTranslation } from '../i18n';

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Select } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { userPath, adminPath, hasUserMode, hasAdminMode } from '../config/paths';
import phoneCountryCodes from '../utils/phoneCountryCodes';
import AuthLayout from '../components/AuthLayout';
import InlineCaptcha from '../components/InlineCaptcha';
import OAuthButtons from '../components/OAuthButtons';
import { affiliateCode } from '../utils/affiliate';
import CountryFlag from '../components/CountryFlag';

const { Option } = Select;

const Register = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { register, currentUser } = useAuth();
    const navigate = useNavigate();

    // 如果用户已登录，重定向到主页
    React.useEffect(() => {
        if (currentUser) {
            // 根据角色和构建模式重定向
            if (currentUser.role === 'admin' && hasAdminMode()) {
                navigate(adminPath('/dashboard'), { replace: true });
            } else if (hasUserMode()) {
                navigate(userPath('/dashboard'), { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [currentUser, navigate]);

    const onFinish = async (values) => {
        try {
            setLoading(true);
            const { confirm, ...userData } = values;
            userData.aff = affiliateCode();

            const result = await register(userData);

            if (result.success) {
                window.dispatchEvent(new CustomEvent('yc:signup', { detail: { method: 'email' } }));
                // 成功提示由 AuthContext.register 统一弹出（含后端真实消息，
                // 如"请等待管理员审核"），此处不再重复 toast，仅跳转登录页。
                navigate('/login');
            }
        } catch (error) {
            message.error(t('Registration failed: {{value}}', { value: error.message || t('Unknown error') }));
        } finally {
            setLoading(false);
        }
    };

    const phoneCountryCodeSelector = (
        <Form.Item name="phone_country_code" noStyle>
            <Select
                style={{ width: 130 }}
                showSearch
                filterOption={(input, option) =>
                    String(option.search || '').toLowerCase().includes(input.toLowerCase())
                }
            >
                {phoneCountryCodes.map(item => (
                    <Option key={item.code} value={item.code} search={`${item.code} ${item.short} ${item.label}`}>
                        <CountryFlag code={item.short} />{item.code} {item.short}
                    </Option>
                ))}
            </Select>
        </Form.Item>
    );

    return (
        <AuthLayout title={t('Create account')} subtitle={t('Create an account and get cloud services running in minutes')}>

                <Form
                    name="register"
                    initialValues={{ phone_country_code: '+86' }}
                    onFinish={onFinish}
                    size="large"
                >
                    <Form.Item
                        name="username"
                        rules={[
                            { required: true, message: t('Enter a username!') },
                            { min: 3, message: t('Username must be at least 3 characters!') }
                        ]}
                    >
                        <Input prefix={<UserOutlined />} placeholder={t('Username')} />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: t('Enter an email address!') },
                            { type: 'email', message: t('Enter a valid email address"\'!') }
                        ]}
                    >
                        <Input prefix={<MailOutlined />} placeholder={t('Email')} />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                    >
                        <Input
                            addonBefore={phoneCountryCodeSelector}
                            placeholder={t('Mobile number (optional)')}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: t('Please enter a password!') },
                            { min: 8, message: t('Password must be at least 8 characters!') },
                            { pattern: /^(?=.*[A-Za-z])(?=.*\d)/, message: t('Password must contain both letters and numbers!') }
                        ]}
                        hasFeedback
                        extra={t('At least 8 characters, including both letters and numbers')}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder={t('Password')} />
                    </Form.Item>

                    <Form.Item
                        name="confirm"
                        dependencies={['password']}
                        hasFeedback
                        rules={[
                            { required: true, message: t('Please confirm your password!') },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('Passwords do not match!')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder={t('Confirm password')} />
                    </Form.Item>

                    <InlineCaptcha action="register" />
                    <Form.Item>
                        <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={loading}>
                            {t('Register')}
                        </Button>
                        <div style={{ marginTop: 16 }}>
                            <OAuthButtons />
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            {t('Already have an account? ')}<Link to="/login">{t('Sign in now')}</Link>
                        </div>
                    </Form.Item>
                </Form>
        </AuthLayout>
    );
};

export default Register;
